import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import UserSettingsService from "src/services/userSettingsService";
import { RootState, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types";
import { selectGroupsByIds } from "@dashboard/groups/store/selectors";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SidePanel } from "@dashboard/shared/components/SidePanel/SidePanel";
import { SectionDescription } from "@dashboard/shared/styles";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";

import { RolloutTable } from "./components/RolloutTable/RolloutTable";
import { VersionTable } from "./components/VersionTable/VersionTable";

const useStyles = makeStyles()((theme: Theme) => ({
    hardwareField: {
        marginBottom: theme.spacing(4),
    },
}));

function getHardwareOptions(groups: Group[]) {
    const products = groups.flatMap((group) => group.statistics?.products ?? []);

    return _.orderBy(
        _.uniqBy(
            products.filter((product) => product.codename),
            (product) => product.codename,
        ).map((product) => ({
            label: product.productName || product.codename,
            value: product.codename,
        })),
        ["label"],
    );
}

const RolloutsPage = () => {
    const { t } = useTranslation(["pages"]);
    const { classes } = useStyles();

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const groups = useSelector((state: RootState) => selectGroupsByIds(state, groupIds));
    const hardwareOptions = getHardwareOptions(groups);
    const [selectedHardware, setSelectedHardware] = React.useState<string | null>(() =>
        UserSettingsService.getRolloutsPageLastSelectedHardware(),
    );
    const isSelectionAvailable = hardwareOptions.some((option) => option.value === selectedHardware);
    const hardware = (isSelectionAvailable ? selectedHardware : null) ?? hardwareOptions[0]?.value ?? null;

    const onHardwareChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (selectedOption && typeof selectedOption.value === "string") {
            setSelectedHardware(selectedOption.value);
            UserSettingsService.setWorkspaceValue("rolloutsPage.lastSelectedHardware", selectedOption.value);
        }
    };

    const isHardwareSelectDisabled = hardwareOptions.length === 0;

    return (
        <PageContainer>
            {workspaceId ? (
                <ExpiredWrapper>
                    <React.Fragment>
                        <SelectField
                            label={t("pages:rolloutsPage.input.hardware.caption")}
                            placeholder={t("pages:rolloutsPage.noHardware")}
                            value={hardwareOptions.find((option) => option.value === hardware)}
                            options={hardwareOptions}
                            onChange={onHardwareChange}
                            disabled={isHardwareSelectDisabled}
                            disabledTooltip={t("pages:rolloutsPage.noHardwareTooltip")}
                            fullWidth
                            className={classes.hardwareField}
                            disableSorting
                            searchable
                        />

                        <CollapsibleProvider initialValue={true}>
                            <CollapsibleSectionSummary>
                                <CollapsibleSectionTitle>{t("pages:rolloutsPage.versionList")}</CollapsibleSectionTitle>
                            </CollapsibleSectionSummary>
                            <CollapsibleSectionDetails>
                                <SectionDescription>{t("pages:rolloutsPage.versionListDescription")}</SectionDescription>
                                <VersionTable codename={hardware} workspaceId={workspaceId} />
                            </CollapsibleSectionDetails>
                        </CollapsibleProvider>

                        <CollapsibleProvider initialValue={true}>
                            <CollapsibleSectionSummary>
                                <CollapsibleSectionTitle>{t("pages:rolloutsPage.rolloutList")}</CollapsibleSectionTitle>
                            </CollapsibleSectionSummary>
                            <CollapsibleSectionDetails>
                                <SectionDescription>{t("pages:rolloutsPage.rolloutListDescription")}</SectionDescription>
                                <RolloutTable codename={hardware} workspaceId={workspaceId} />
                            </CollapsibleSectionDetails>
                        </CollapsibleProvider>
                    </React.Fragment>
                </ExpiredWrapper>
            ) : null}
            <SidePanel />
        </PageContainer>
    );
};

export default withMetaDescription(RolloutsPage, {
    title: i18next.t("pages:rolloutsPage.metaTitle"),
});
